import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { LOGO_URL, presaleConfig } from '../../data/mockData';
import { Zap, Wallet, Users, DollarSign, Copy, Check, ExternalLink } from 'lucide-react';

const DashboardHome = () => {
  const { user } = useAuth();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, mins: 0, secs: 0 });
  const [copied, setCopied] = useState(false);

  const calculateTimeLeft = useCallback(() => {
    const end = new Date(presaleConfig.endDate).getTime();
    const now = Date.now();
    const diff = end - now;
    if (diff <= 0) return { days: 0, hours: 0, mins: 0, secs: 0 };
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      mins: Math.floor((diff / (1000 * 60)) % 60),
      secs: Math.floor((diff / 1000) % 60),
    };
  }, []);

  useEffect(() => {
    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, [calculateTimeLeft]);

  const pad = (n) => String(n).padStart(2, '0');

  const referralLink = `https://marsupilami.coin/ref/${user?.referralCode || 'XXXX'}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const dexLinks = [
    { name: 'Bitget DEX', icon: 'B' },
    { name: 'OKX DEX', icon: 'O' },
    { name: 'Binance DEX', icon: 'B' },
    { name: 'Bybit DEX', icon: 'By' },
  ];

  return (
    <div>
      {/* Welcome */}
      <h1 className="text-2xl lg:text-3xl font-bold text-yellow-400 mb-6">
        Welcome, {user?.username || 'User'}!
      </h1>

      {/* Stage + DEX + Countdown Row */}
      <div className="bg-[#0d1117] border border-[#1a2333] rounded-2xl p-5 mb-6">
        <div className="flex flex-col lg:flex-row items-center gap-6">
          {/* Stage Info */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center">
              <Zap size={20} className="text-yellow-400" />
            </div>
            <div>
              <p className="text-white font-bold">{presaleConfig.stageName} Live</p>
              <p className="text-gray-400 text-sm">Current Price: <span className="text-yellow-400 font-bold">${presaleConfig.price}</span></p>
            </div>
          </div>

          {/* DEX Links */}
          <div className="flex-1 flex justify-center">
            <div className="grid grid-cols-2 gap-2 border border-yellow-400/30 rounded-xl p-3">
              {dexLinks.map(dex => (
                <button key={dex.name} className="flex items-center gap-2 bg-[#1a2333] hover:bg-[#243040] rounded-lg px-4 py-2 text-gray-300 text-sm transition-colors">
                  <div className="w-5 h-5 rounded-full bg-gray-600 flex items-center justify-center text-[10px] text-white font-bold">{dex.icon}</div>
                  {dex.name}
                </button>
              ))}
            </div>
          </div>

          {/* Countdown */}
          <div className="text-center">
            <p className="text-yellow-400 text-xs mb-2">{pad(timeLeft.days)} Days Left</p>
            <div className="flex items-center gap-3">
              <div className="text-center">
                <p className="text-white text-2xl font-bold font-mono">{pad(timeLeft.hours)}</p>
                <p className="text-gray-500 text-[10px]">HOURS</p>
              </div>
              <div className="text-center">
                <p className="text-white text-2xl font-bold font-mono">{pad(timeLeft.mins)}</p>
                <p className="text-gray-500 text-[10px]">MINUTES</p>
              </div>
              <div className="text-center">
                <p className="text-white text-2xl font-bold font-mono">{pad(timeLeft.secs)}</p>
                <p className="text-gray-500 text-[10px]">SECONDS</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Total Holdings */}
        <div className="bg-[#0d1117] border border-[#1a2333] rounded-2xl p-5 relative overflow-hidden">
          <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Total Holdings</p>
          <span className="inline-block bg-yellow-400/20 text-yellow-400 text-[10px] font-bold px-2 py-0.5 rounded-md mb-3">WALLET</span>
          <p className="text-white text-3xl font-bold">{user?.holdings || 0}</p>
          <p className="text-gray-500 text-sm">MRS</p>
          <img src={LOGO_URL} alt="" className="absolute -right-4 -bottom-4 w-24 h-24 opacity-20 rounded-full" />
        </div>

        {/* Network */}
        <div className="bg-[#0d1117] border border-[#1a2333] rounded-2xl p-5 relative overflow-hidden">
          <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Network</p>
          <span className="inline-block bg-purple-400/20 text-purple-400 text-[10px] font-bold px-2 py-0.5 rounded-md mb-3">REFERRALS</span>
          <p className="text-white text-3xl font-bold">{user?.referrals || 0}</p>
          <p className="text-gray-500 text-sm">Active Users</p>
        </div>

        {/* Referral Earnings */}
        <div className="bg-[#0d1117] border border-[#1a2333] rounded-2xl p-5 relative overflow-hidden">
          <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Referral Earnings</p>
          <span className="inline-block bg-green-400/20 text-green-400 text-[10px] font-bold px-2 py-0.5 rounded-md mb-3">DIVIDENDS</span>
          <p className="text-white text-3xl font-bold">${user?.earnings || 0}</p>
          <p className="text-gray-500 text-sm">USDT Accumulated</p>
        </div>
      </div>

      {/* Referral Link */}
      <div className="bg-[#0d1117] border border-[#1a2333] rounded-2xl p-5">
        <h3 className="text-white font-bold text-lg mb-3">Your Referral Link</h3>
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-[#060d06] border border-[#1a3a1a] rounded-lg px-4 py-2.5 text-yellow-400 text-sm font-mono truncate">
            {referralLink}
          </div>
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 bg-yellow-400/10 border border-yellow-400/30 rounded-lg px-4 py-2.5 text-yellow-400 hover:bg-yellow-400/20 transition-colors text-sm"
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
